// useError.js - Performance optimized error handling hook
import { useContext, useCallback } from 'react';
import { ErrorContext, ERROR_TYPES } from './ErrorContext';
import { useNotification } from '../Notifications';

const useError = () => {
  const {
    errorState,
    handleApiError,
    getErrorMessage,
    clearErrors,
    clearError,
    clearValidationErrors,
    setValidationErrors
  } = useContext(ErrorContext);
  
  const notification = useNotification();

  // Process API errors and show notifications - MEMOIZED
  const processApiError = useCallback((error, options = {}) => {
    const { showNotification = true, notificationType = 'error' } = options;
    
    // First, handle the error in the context
    handleApiError(error);
    
    // If notifications are enabled, display appropriate notification
    if (showNotification && error) {
      // Get the error code and message
      const code = error.code || ERROR_TYPES.SERVER;
      const message = error.error || error.message || getErrorMessage(code);
      
      // Show notification based on error type
      switch (code) {
        case ERROR_TYPES.RATE_LIMIT:
          notification.warning(message);
          break;
          
        case ERROR_TYPES.VALIDATION:
          // Don't show toast for validation errors - they are displayed inline
          break;
          
        case ERROR_TYPES.SESSION_EXPIRED:
        case ERROR_TYPES.INVALID_CREDENTIALS:
        case ERROR_TYPES.USER_NOT_FOUND:
        case ERROR_TYPES.PASSWORD_REQUIREMENTS:
        case ERROR_TYPES.INVALID_OTP:
        case ERROR_TYPES.WRONG_PROVIDER:
        case ERROR_TYPES.EMAIL_EXISTS:
          notification.warning(message);
          break;
          
        case ERROR_TYPES.NETWORK:
          notification.error('Network connection issue. Please check your internet connection and try again.');
          break;
          
        case ERROR_TYPES.GOOGLE_SIGNIN_FAILED:
        case ERROR_TYPES.GOOGLE_INFO_FAILED:
        case ERROR_TYPES.ACCOUNT_CREATION_FAILED:
        case ERROR_TYPES.ACCOUNT_SETUP_FAILED:
        case ERROR_TYPES.ACCOUNT_UPDATE_FAILED:
          notification.error(message);
          break;
          
        case ERROR_TYPES.AUTO_LOGIN_FAILED:
          notification.warning(message);
          break;
          
        case ERROR_TYPES.EMAIL_SEND_FAILED:
          notification.error(message);
          break;
          
        // Firewall error notification handling
        case ERROR_TYPES.FIREWALL_CREATE_FAILED:
        case ERROR_TYPES.FIREWALL_DELETE_FAILED:
        case ERROR_TYPES.FIREWALL_RULE_ADD_FAILED:
        case ERROR_TYPES.FIREWALL_RULE_DELETE_FAILED:
        case ERROR_TYPES.FIREWALL_ATTACH_FAILED:
        case ERROR_TYPES.FIREWALL_DETACH_FAILED:
        case ERROR_TYPES.FIREWALL_LIST_FAILED:
        case ERROR_TYPES.FIREWALL_DETAILS_FAILED:
          notification.error(message);
          break;
          
        case ERROR_TYPES.INVALID_RULE_PARAMETERS:
        case ERROR_TYPES.REGION_NOT_AVAILABLE:
          notification.warning(message);
          break;
          
        // Volume error notification handling
        case ERROR_TYPES.VOLUME_CREATE_FAILED:
        case ERROR_TYPES.VOLUME_DELETE_FAILED:
        case ERROR_TYPES.VOLUME_ATTACH_FAILED:
        case ERROR_TYPES.VOLUME_DETACH_FAILED:
        case ERROR_TYPES.VOLUME_LIST_FAILED:
        case ERROR_TYPES.VOLUME_DETAILS_FAILED:
        case ERROR_TYPES.VOLUME_NOT_FOUND:
          notification.error(message);
          break;
        
        case ERROR_TYPES.INVALID_VOLUME_PARAMETERS:
        case ERROR_TYPES.VOLUME_ALREADY_EXISTS:
        case ERROR_TYPES.VOLUME_IN_USE:
        case ERROR_TYPES.VOLUME_SIZE_INVALID:
        case ERROR_TYPES.VM_NOT_ACTIVE:
          notification.warning(message);
          break;

        // VM deployment error notification handling
        case ERROR_TYPES.VM_DEPLOY_FAILED:
          notification.error(message || 'Failed to deploy VM. Please try again later.');
          break;
          
        case ERROR_TYPES.VM_DELETE_FAILED:
        case ERROR_TYPES.VM_START_FAILED:
        case ERROR_TYPES.VM_STOP_FAILED:
        case ERROR_TYPES.VM_REBOOT_FAILED:
        case ERROR_TYPES.VM_HIBERNATE_FAILED:
        case ERROR_TYPES.VM_RESTORE_FAILED:
        case ERROR_TYPES.VM_RESIZE_FAILED:
        case ERROR_TYPES.VM_STATUS_CHECK_FAILED:
        case ERROR_TYPES.VM_LIST_FAILED:
        case ERROR_TYPES.VM_DETAILS_FAILED:
          notification.error(message);
          break;
          
        case ERROR_TYPES.INSUFFICIENT_CREDITS:
          notification.warning(message, { duration: 10000 }); // Longer duration for important message
          break;
          
        case ERROR_TYPES.GPU_NOT_AVAILABLE:
        case ERROR_TYPES.FLAVOR_NOT_FOUND:
        case ERROR_TYPES.REGION_CAPACITY_EXCEEDED:
          notification.warning(message);
          break;
          
        case ERROR_TYPES.SSH_KEY_REQUIRED:
        case ERROR_TYPES.ADMIN_PASSWORD_REQUIRED:
        case ERROR_TYPES.OS_IMAGE_REQUIRED:
        case ERROR_TYPES.INVALID_VM_PARAMETERS:
        case ERROR_TYPES.DEPLOYMENT_VALIDATION_FAILED:
          notification.warning(message);
          break;
          
        case ERROR_TYPES.SECURITY_RULE_APPLICATION_FAILED:
          notification.warning(`${message} You can add security rules later from the VM details page.`);
          break;
          
        case ERROR_TYPES.CLOUD_INIT_SCRIPT_INVALID:
        case ERROR_TYPES.JUPYTER_PASSWORD_WEAK:
          notification.warning(message);
          break;
          
        case ERROR_TYPES.CALLBACK_REGISTRATION_FAILED:
          // Less critical error, just show info notification
          notification.info(message || 'Status update registration failed. VM status updates may be delayed.');
          break;
          
        case ERROR_TYPES.DEPLOYMENT_TIMEOUT:
          notification.warning(message, { duration: 8000 });
          break;
          
        case ERROR_TYPES.FLAVOR_INCOMPATIBLE:
          notification.warning(message);
          break;
          
        // Image error notification handling
        case ERROR_TYPES.IMAGE_CREATE_FAILED:
        case ERROR_TYPES.IMAGE_DELETE_FAILED:
        case ERROR_TYPES.IMAGE_LIST_FAILED:
        case ERROR_TYPES.IMAGE_DETAILS_FAILED:
        case ERROR_TYPES.IMAGE_NOT_FOUND:
          notification.error(message);
          break;
          
        case ERROR_TYPES.INVALID_IMAGE_PARAMETERS:
        case ERROR_TYPES.IMAGE_ALREADY_EXISTS:
        case ERROR_TYPES.NO_SNAPSHOTS_AVAILABLE:
        case ERROR_TYPES.SNAPSHOT_NOT_FOUND:
          notification.warning(message);
          break;
          
        // Snapshot error notification handling
        case ERROR_TYPES.SNAPSHOT_CREATE_FAILED:
        case ERROR_TYPES.SNAPSHOT_DELETE_FAILED:
        case ERROR_TYPES.SNAPSHOT_RESTORE_FAILED:
        case ERROR_TYPES.SNAPSHOT_LIST_FAILED:
        case ERROR_TYPES.SNAPSHOT_DETAILS_FAILED:
        case ERROR_TYPES.SNAPSHOT_NOT_FOUND:
          notification.error(message);
          break;
          
        case ERROR_TYPES.INVALID_SNAPSHOT_PARAMETERS:
        case ERROR_TYPES.SNAPSHOT_ALREADY_EXISTS:
        case ERROR_TYPES.SNAPSHOT_IN_USE:
        case ERROR_TYPES.VM_UNAVAILABLE:
        case ERROR_TYPES.VM_NOT_COMPATIBLE:
        case ERROR_TYPES.NO_ACTIVE_VMS:
          notification.warning(message);
          break;
          
        // Profile/Account error notification handling
        case ERROR_TYPES.PROFILE_UPDATE_FAILED:
        case ERROR_TYPES.PROFILE_FETCH_FAILED:
        case ERROR_TYPES.ACCOUNT_SETTINGS_SAVE_FAILED:
        case ERROR_TYPES.PAYMENT_METHOD_ADD_FAILED:
        case ERROR_TYPES.PAYMENT_METHOD_REMOVE_FAILED:
        case ERROR_TYPES.PAYMENT_METHOD_UPDATE_FAILED:
        case ERROR_TYPES.BILLING_HISTORY_FETCH_FAILED:
        case ERROR_TYPES.EMAIL_VERIFICATION_FAILED:
        case ERROR_TYPES.PROFILE_PICTURE_UPLOAD_FAILED:
          notification.error(message);
          break;

        // Payment error notification handling
        case ERROR_TYPES.PAYMENT_INITIALIZATION_FAILED:
        case ERROR_TYPES.RAZORPAY_SDK_LOAD_FAILED:
        case ERROR_TYPES.PAYMENT_ORDER_CREATION_FAILED:
        case ERROR_TYPES.RAZORPAY_API_ERROR:
        case ERROR_TYPES.STRIPE_API_ERROR:
          notification.error(message);
          break;
          
        case ERROR_TYPES.PAYMENT_PROCESSING_FAILED:
        case ERROR_TYPES.PAYMENT_VERIFICATION_FAILED:
        case ERROR_TYPES.PAYMENT_CAPTURE_FAILED:
        case ERROR_TYPES.CREDIT_PURCHASE_FAILED:
          notification.error(message);
          break;
          
        case ERROR_TYPES.TRANSACTION_RETRIEVAL_FAILED:
          notification.error(message || 'Failed to retrieve transaction history. Please try again later.');
          break;
          
        case ERROR_TYPES.PAYMENT_CURRENCY_NOT_SUPPORTED:
        case ERROR_TYPES.PAYMENT_AMOUNT_TOO_LOW:
        case ERROR_TYPES.PAYMENT_SIGNATURE_VERIFICATION_FAILED:
          notification.warning(message);
          break;
          
        case ERROR_TYPES.PAYMENT_GATEWAY_ERROR:
        case ERROR_TYPES.PAYMENT_WEBHOOK_PROCESSING_FAILED:
          notification.error(message);
          break;
          
        case ERROR_TYPES.PASSWORD_CHANGE_FAILED:
        case ERROR_TYPES.PASSWORD_MISMATCH:
        case ERROR_TYPES.OLD_PASSWORD_INCORRECT:
        case ERROR_TYPES.TWO_FACTOR_SETUP_FAILED:
        case ERROR_TYPES.TWO_FACTOR_DISABLE_FAILED:
        case ERROR_TYPES.NOTIFICATION_SETTINGS_UPDATE_FAILED:
          notification.warning(message);
          break;
          
        default:
          if (notificationType === 'error') {
            notification.error(message);
          } else if (notificationType === 'warning') {
            notification.warning(message);
          } else if (notificationType === 'info') {
            notification.info(message);
          }
      }
    }
    
    return error;
  }, [handleApiError, getErrorMessage, notification]);
  
  // Handle VM deployment-specific errors with proper notification types - MEMOIZED
  const handleDeploymentError = useCallback((error) => {
    if (!error) return;
    
    // Add deployment-specific error code if not present
    if (!error.code) {
      if (error.message && error.message.includes('insufficient credits')) {
        error.code = ERROR_TYPES.INSUFFICIENT_CREDITS;
      } else if (error.message && error.message.includes('GPU not available')) {
        error.code = ERROR_TYPES.GPU_NOT_AVAILABLE;
      } else if (error.message && error.message.includes('flavor not found')) {
        error.code = ERROR_TYPES.FLAVOR_NOT_FOUND;
      } else if (error.message && error.message.includes('capacity exceeded')) {
        error.code = ERROR_TYPES.REGION_CAPACITY_EXCEEDED;
      } else if (error.message && error.message.includes('SSH key')) {
        error.code = ERROR_TYPES.SSH_KEY_REQUIRED;
      } else if (error.message && error.message.includes('admin password')) {
        error.code = ERROR_TYPES.ADMIN_PASSWORD_REQUIRED;
      } else if (error.message && error.message.includes('OS image')) {
        error.code = ERROR_TYPES.OS_IMAGE_REQUIRED;
      } else if (error.message && error.message.includes('validation failed')) {
        error.code = ERROR_TYPES.DEPLOYMENT_VALIDATION_FAILED;
      } else if (error.message && error.message.includes('security rule')) {
        error.code = ERROR_TYPES.SECURITY_RULE_APPLICATION_FAILED;
      } else if (error.message && error.message.includes('cloud-init')) {
        error.code = ERROR_TYPES.CLOUD_INIT_SCRIPT_INVALID;
      } else if (error.message && error.message.includes('Jupyter password')) {
        error.code = ERROR_TYPES.JUPYTER_PASSWORD_WEAK;
      } else if (error.message && error.message.includes('callback')) {
        error.code = ERROR_TYPES.CALLBACK_REGISTRATION_FAILED;
      } else if (error.message && error.message.includes('timeout')) {
        error.code = ERROR_TYPES.DEPLOYMENT_TIMEOUT;
      } else {
        error.code = ERROR_TYPES.VM_DEPLOY_FAILED;
      }
    }
    
    return processApiError(error);
  }, [processApiError]);
  
  // Handle form submission errors with proper validation - MEMOIZED
  const handleFormError = useCallback((error, formRef) => {
    if (!error) return;
    
    // Process API error first
    processApiError(error, { showNotification: false });
    
    // If there are validation errors and we have a form ref, focus the first invalid field
    if (error.fieldErrors && formRef && formRef.current) {
      const fields = Object.keys(error.fieldErrors);
      if (fields.length > 0) {
        const firstField = formRef.current.elements[fields[0]];
        if (firstField) {
          firstField.focus();
        }
      }
      
      // Show a single validation error notification
      notification.warning('Please correct the errors in the form.');
    }
  }, [processApiError, notification]);
  
  // Special handler for VM deployment validation errors - MEMOIZED
  const handleDeploymentValidationError = useCallback((validationErrors) => {
    if (!validationErrors || Object.keys(validationErrors).length === 0) return;
    
    // Set validation errors in context
    setValidationErrors(validationErrors);
    
    // Show a single notification for all validation errors
    const errorCount = Object.keys(validationErrors).length;
    notification.warning(`${errorCount} validation ${errorCount === 1 ? 'error' : 'errors'} found. Please check the form.`);
    
    // Return the first error message for display
    const firstErrorField = Object.keys(validationErrors)[0];
    return validationErrors[firstErrorField];
  }, [setValidationErrors, notification]);
  
  // Clear all errors and notifications - MEMOIZED
  const resetErrors = useCallback(() => {
    clearErrors();
    // Optionally clear any lingering notifications
    // (if your notification system has a clearAll method)
    if (notification.clearAll) {
      notification.clearAll();
    }
  }, [clearErrors, notification]);
  
  // Override renderError function to ensure correct error display priority - MEMOIZED
  const renderError = useCallback(() => {
    const { error, formErrors, hasError } = errorState;
    
    if (!hasError) return null;
    
    // Render the main error if present
    if (error) {
      let title = "Error";
      
      // Customize title based on error type
      switch (error.code) {
        // Auth error titles
        case ERROR_TYPES.RATE_LIMIT:
          title = "Access Temporarily Limited";
          break;
        case ERROR_TYPES.WRONG_PROVIDER:
          title = "Wrong Sign-in Method";
          break;
        case ERROR_TYPES.INVALID_CREDENTIALS:
          title = "Sign-in Failed";
          break;
        case ERROR_TYPES.EMAIL_EXISTS:
          title = "Account Already Exists";
          break;
        case ERROR_TYPES.USER_NOT_FOUND:
          title = "Account Not Found";
          break;
        case ERROR_TYPES.PASSWORD_REQUIREMENTS:
          title = "Password Requirements";
          break;
        case ERROR_TYPES.INVALID_OTP:
          title = "Verification Failed";
          break;
        case ERROR_TYPES.NETWORK:
          title = "Connection Error";
          break;
        case ERROR_TYPES.SESSION_EXPIRED:
          title = "Session Expired";
          break;
          
        // VM deployment error titles  
        case ERROR_TYPES.VM_DEPLOY_FAILED:
          title = "VM Deployment Failed";
          break;
        case ERROR_TYPES.INSUFFICIENT_CREDITS:
          title = "Insufficient Credits";
          break;
        case ERROR_TYPES.GPU_NOT_AVAILABLE:
          title = "GPU Not Available";
          break;
          
        default:
          title = error.title || "Error";
      }
      
      return (
        <div className="auth-error-container">
          <div className="auth-error-header">
            <span>{title}</span>
          </div>
          <p>{error.message}</p>
          {error.action && (
            <button
              onClick={error.actionHandler}
              className="error-action-button"
            >
              {error.action}
            </button>
          )}
        </div>
      );
    }
    
    // If no main error but validation errors exist, show those
    if (Object.keys(formErrors).length > 0) {
      return (
        <div className="auth-error-container">
          <div className="auth-error-header">
            <span>Please Fix the Following Errors</span>
          </div>
          <ul className="auth-error-list">
            {Object.entries(formErrors).map(([field, error]) => (
              <li key={field}>
                {Array.isArray(error) ? error.join(', ') : error}
              </li>
            ))}
          </ul>
        </div>
      );
    }
    
    return null;
  }, [errorState]);
  
  return {
    ...errorState,
    processApiError,
    handleDeploymentError,
    handleFormError,
    handleDeploymentValidationError,
    resetErrors,
    clearError,
    clearValidationErrors,
    setValidationErrors,
    renderError,
    getErrorMessage
  };
};

export default useError;